import { ChangeEvent, forwardRef, useState, useEffect, SyntheticEvent, useMemo } from "react";
import { Search as SearchIcon, ModeEdit as ModeEditIcon, Timeline as TimelineIcon, DeleteForever as DeleteForeverIcon } from '@mui/icons-material';
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { NavLink } from "react-router-dom";
import Title from "../../../components/Title";
import { Office } from "../../../models/office";
import { Collect } from "../../../models/collect";
import { Lend } from "../../../models/lend";
import { Pay } from "../../../models/pay";
import HistoricoModal from './modal/historicpay';
import EditPaysModalWeb from './modal/editpay';
import { useSelection } from '../../../components/context/SelectionContext';
import { FormGroup, FormControlLabel, Checkbox, IconButton, useTheme, useMediaQuery, TextField, CardActions, Divider, Alert, Typography, Button, Box, Card, Grid, FormControl, MenuItem, InputLabel, Select, CardContent, Tooltip } from '@mui/material';
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString } from '../../../utils/operationalDateTime';
import { Add as AddIcon, Info as InfoIcon, AddAlert as AddAlertIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow, SimpleColumnGroup, RowId } from '../../../components/table/interface';
import { useDrawer } from '../../../components/context/DrawerContext';
import { stickyListRegionWidth } from '../../../components/layouts/StickyListToolbar';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import {
  isOperationalDateEligibleForPayDelete,
  isWebSuperAdminPrincipal,
} from '../../../utils/payDeleteEligibility';

const CustomInput = forwardRef((props, ref) => {
  return (
    <TextField
      fullWidth
      {...props}
      inputRef={ref}
      label="Date"
      autoComplete="off"
    />
  );
});

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const InformeAbonosAdd: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [lends, setLends] = useState<Lend[]>([]);
  const [marcado, setMarcado] = useState("");
  const [pays, setPays] = useState<Pay[]>([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [offices, setOffices] = useState<Office[]>([]);
  const [openHistorico, setOpenHistorico] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [payId, setPayId] = useState<string | null>(null);
  const [payValue, setPayValue] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [fullname, setFullname] = useState<string | null>(null);
  const [collectId, setCollectId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [payDate, setPayDate] = useState<string | null>(null);
  const { office_id, setOffice_id } = useSelection();
  const { drawerOpen } = useDrawer(); // ya lo usas en tu componente
  const { canRead, canUpdate, canDelete, authUserData } =
    useResourcePermissions('/prestamos/abonosporfecha');
  const isSuperAdmin = isWebSuperAdminPrincipal(authUserData);

  const getLocalDate = () => nowOperationalDateString();
  const currentOperationalDate = getLocalDate();

  const [start_date, setStart_date] = useState(getLocalDate());
  const [end_date, setEnd_date] = useState(getLocalDate());

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        showLoading();
        const { data } = await axios.get("offices");
        setOffices(data.data);
        showAlert('Se ha realizado la consulta exitosamente!', 'success');
      } catch (error) {
        console.error('Error fetching offices:', error);
      } finally {
        hideLoading();
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchCollects = async () => {
      if (office_id) {
        try {
          const { data } = await axios.get(`collectsoffice/${office_id}`);
          setCollectsOffice(data);
          setSelectedCollects([]);
          setMarcado("");
          setLends([]);
          setPays([]);
          setSelectAll(false);
        } catch (error) {
          console.error("Error fetching collects:", error);
        }
      }
    };

    fetchCollects();
  }, [office_id]);

  useEffect(() => {
    const fetchLends = async () => {
      if (marcado) {
        try {
          const { data } = await axios.get(`lendcollect/${marcado}`);
          setLends(data.data);
        } catch (error) {
          console.error("Error fetching lends:", error);
        }
      }
    };

    fetchLends();
  }, [marcado]);
  const fetchPays = async () => {
    try {
      showLoading();
      setPays([]);
      const response = await axios.post("/report/paybydate", {
        end_date,
        start_date,
        collect_id: selectedCollects,
      });
      setPays(response.data.data);
      if (response.data.data.length === 0) {
        showAlert('No se encuentran registros!', 'info');
      } else {
        showAlert('Se ha realizado la consulta exitosamente!', 'success');
      }
    } catch (error) {
      console.error("paybydate:", error);
      const msg =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof (error.response.data as { message?: string }).message === "string"
          ? (error.response.data as { message: string }).message
          : null;
      showAlert(msg || "Error al consultar los abonos.", "error");
    } finally {
      hideLoading();
    }
  };
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!office_id || !start_date || !end_date || selectedCollects.length === 0) {
      showAlert('Tiene datos pendientes por diligenciar', 'warning');
      return;
    }

    const formData = {
      end_date,
      start_date,
      collect_id: selectedCollects,
    };

    const url = "/report/paybydate";
    try {
      showLoading();
      setPays([]);
      const response = await axios.post(url, formData);
      const data = response.data.data;
      setPays(data);
      if (data.length === 0) {
        showAlert('No se encuentran registros!', 'info');
      } else {
        showAlert('Se ha realizado la consulta exitosamente!', 'success');
      }
    } catch (error) {
      console.error("paybydate:", error);
      const msg =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof (error.response.data as { message?: string }).message === "string"
          ? (error.response.data as { message: string }).message
          : null;
      showAlert(msg || "Error al consultar los abonos.", "error");
    } finally {
      hideLoading();
    }
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedCollects((prevSelectedCollects) =>
      prevSelectedCollects.includes(value)
        ? prevSelectedCollects.filter((id) => id !== value)
        : [...prevSelectedCollects, value]
    );

    if (!event.target.checked && selectAll) {
      setSelectAll(false);
    }
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setSelectAll(checked);
    setSelectedCollects(checked ? collectsOffice.map((collect) => collect.id.toString()) : []);
  };


  const handleDeletePay = async (
    payIdValue: number | string | null | undefined,
    payDateValue?: string | null
  ) => {
    const id = Number(payIdValue);
    if (Number.isNaN(id)) {
      showAlert('ID de abono inválido.', 'warning');
      return;
    }
    if (
      payDateValue &&
      !isOperationalDateEligibleForPayDelete({
        payDateYmd: payDateValue,
        todayYmd: currentOperationalDate,
        superAdmin: isSuperAdmin,
      })
    ) {
      showAlert(
        isSuperAdmin
          ? 'Como administrador sólo puede eliminar abonos de los últimos 30 días (fecha operativa).'
          : 'Sólo se pueden eliminar abonos de la fecha actual.',
        'warning',
      );
      return;
    }
    if (!window.confirm('¿Está seguro de eliminar este abono?')) return;

    try {
      showLoading();
      await axios.post('/pays/delete', { id });
      showAlert('Abono eliminado correctamente.', 'success');
      await fetchPays();
    } catch (error) {
      console.error('Error eliminando abono:', error);
      const msg =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof (error.response.data as { message?: string }).message === 'string'
          ? (error.response.data as { message: string }).message
          : null;
      showAlert(msg || 'Error al eliminar el abono.', 'error');
    } finally {
      hideLoading();
    }
  };

  const decimalFormatter = useMemo(() => new Intl.NumberFormat('en-US', { style: 'decimal' }), []);

  const fullColumns: SimpleColumn[] = [
    { field: 'collect', headerName: 'Cobro', minWidth: 80,  maxWidth: 100, wrapText: true },
    { field: 'id_client', headerName: 'Cédula', minWidth: 80,  maxWidth: 100, },
    { field: 'fullname', headerName: 'Cliente', minWidth: 100, maxWidth: 130, wrapText: true },
    {
      field: 'value',
      headerName: 'Valor',
      minWidth: 60,
      maxWidth: 80,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) =>
        (row as any).isGroupSeparator ? '' : decimalFormatter.format(Number(row.value ?? 0)),
    },
    {
      field: 'discount',
      headerName: 'Descuento',
      minWidth: 60,
      maxWidth: 80,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) =>
        (row as any).isGroupSeparator ? '' : decimalFormatter.format(Number(row.discount ?? 0)),
    },
    { field: 'date', headerName: 'Fecha', minWidth: 60, maxWidth: 60 },
    { field: 'time_date', headerName: 'Hora', minWidth: 60, maxWidth: 60 },
    {
      field: 'invoice',
      headerName: 'No Recibo',
      minWidth: 110,
      maxWidth: 140,
      wrapText: true,
    },
    {
      field: 'receipt_bank',
      headerName: 'No Recibo Banco',
      minWidth: 120,
      maxWidth: 140,
      wrapText: true,
    },
    { field: 'user', headerName: 'Usuario', minWidth: 60, maxWidth: 90, wrapText: true, align: 'right' },
    {
      field: 'opt_history',
      headerName: '',
      minWidth: '35px',
      maxWidth: '35px',
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) =>
        (row as any).isGroupSeparator || (row as any).isTotal ? null : (
        <PermissionButton
          allowed={canRead}
          deniedTooltip="Sin permiso para mostrar historial"
          tooltip="Historial"
          variant="contained"
          color="primary"
          onClick={() => {
            setOpenHistorico(true);
            setClientId(String(row.id_client ?? ''));
            setCollectId(row.collect_id != null ? String(row.collect_id) : null);
          }}
          sx={{
            minWidth: 'unset',
            width: '30px',
            padding: '3px',
            boxSizing: 'border-box',
          }}
        >
          <TimelineIcon />
        </PermissionButton>
      ),
    },
    {
      field: 'opt_edit',
      headerName: '',
      minWidth: '35px',
      maxWidth: '35px',
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) =>
        !(row as any).isGroupSeparator && !(row as any).isTotal && row.date === currentOperationalDate ? (
          <PermissionButton
            allowed={canUpdate}
            deniedTooltip="Sin permiso para editar abonos"
            tooltip="Editar"
            variant="contained"
            color="primary"
            onClick={() => {
              setOpenEditar(true);
              setPayId(row.payid != null ? String(row.payid) : null);
              setPayValue(Number(row.value ?? 0));
              setBalance(Number(row.balance ?? 0));
              setFullname(String(row.fullname ?? ''));
              setClientId(String(row.id_client ?? ''));
              setPayDate(String(row.date ?? ''));
            }}
            sx={{
              minWidth: 'unset',
              width: '30px',
              padding: '3px',
              boxSizing: 'border-box',
            }}
          >
            <EditIcon />
          </PermissionButton>
        ) : null,
    },
    {
      field: 'opt_delete',
      headerName: '',
      minWidth: '35px',
      maxWidth: '35px',
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) =>
        !(row as any).isGroupSeparator &&
        !(row as any).isTotal &&
        isOperationalDateEligibleForPayDelete({
          payDateYmd: String(row.date ?? ''),
          todayYmd: currentOperationalDate,
          superAdmin: isSuperAdmin,
        }) ? (
          <PermissionButton
            allowed={canDelete}
            deniedTooltip="Sin permiso para eliminar abonos"
            tooltip="Eliminar"
            variant="contained"
            color="error"
            onClick={() => handleDeletePay(row.payid, String(row.date ?? ''))}
            sx={{
              minWidth: 'unset',
              width: '30px',
              padding: '3px',
              boxSizing: 'border-box',
            }}
          >
            <DeleteIcon />
          </PermissionButton>
        ) : null,
    },
  ];

  const convertirFechaHoraBogota = (isoDate: string) => {
    const fechaBogota = moment.utc(isoDate).tz('America/Bogota');
    return {
      fecha: fechaBogota.format('YYYY-MM-DD'),
      hora: fechaBogota.format('HH:mm:ss'),
    };
  };

  const mobileColumns: SimpleColumn[] = [
    fullColumns[0],
    fullColumns[2],
    fullColumns[3],
    fullColumns[5],
    fullColumns[6],
    fullColumns[7],
    fullColumns[8],
    fullColumns[9],
    fullColumns[10],
    fullColumns[11],
    fullColumns[12],
  ];

  const dataRows: SimpleRow[] = [...pays]
    .sort((a, b) => {
      const collectCompare = String(a.lend.collect.name || '').localeCompare(
        String(b.lend.collect.name || ''),
        'es',
        { sensitivity: 'base' }
      );
      if (collectCompare !== 0) return collectCompare;
      return moment(b.date_operation).valueOf() - moment(a.date_operation).valueOf();
    })
    .map((pay: Pay) => {
      const { fecha, hora } = convertirFechaHoraBogota(pay.date_operation);
      return {
        id: pay.id as RowId,
        payid: pay.id,
        collect: pay.lend.collect.name,
        id_client: pay.lend.client.idnumber,
        fullname: pay.lend.client.fullname,
        discount: pay.value_discount,
        value: pay.value,
        date: fecha,
        time_date: hora,
        invoice: pay.invoice || '',
        receipt_bank: pay.receipt_bank || '',
        late: pay.days_late,
        balance: pay.balance,
        user: pay.user.username,
        collect_id: pay.lend.collect.id,
      };
    });

  const totalValue = dataRows.reduce((sum, row) => Number(sum + (Number(row.value) || 0)), 0);
  const totalDiscount = dataRows.reduce((sum, row) => Number(sum + (Number(row.discount) || 0)), 0);

  const rows: SimpleRow[] = [];
  let currentCollectKey = '';
  dataRows.forEach((row, index) => {
    const nextCollectKey = String(row.collect_id ?? row.collect ?? '').trim();
    if (nextCollectKey !== currentCollectKey) {
      rows.push({
        id: `group-${nextCollectKey || index}` as RowId,
        payid: '',
        collect: row.collect,
        id_client: '',
        fullname: '',
        discount: '',
        value: '',
        date: '',
        time_date: '',
        invoice: '',
        receipt_bank: '',
        late: '',
        balance: '',
        user: '',
        collect_id: row.collect_id,
        isGroupSeparator: true,
      });
      currentCollectKey = nextCollectKey;
    }
    rows.push(row);
  });

  rows.push({
    id: 'TOTAL' as RowId,
    payid: '',
    collect: '',
    id_client: '',
    fullname: 'Total',
    discount: totalDiscount,
    value: totalValue,
    date: '',
    time_date: '',
    late: '',
    balance: '',
    user: '',
    collect_id: '',
    isTotal: true,
  });

  const columnGroups: SimpleColumnGroup[] = [
    { header: 'Opciones', fields: ['opt_history', 'opt_edit', 'opt_delete'] },
  ];

  const displayColumns = isMobile ? mobileColumns : fullColumns;
  const displayColumnGroups = isMobile
    ? columnGroups.map(group => ({
        header: group.header,
        fields: group.fields.filter(field => mobileColumns.some(col => col.field === field)),
      })).filter(group => group.fields.length > 0)
    : columnGroups;

  const handleCloseHistorico = () => {
    setClientId(null);
    setCollectId(null);
    setOpenHistorico(false);
  };
  const handleCloseEdit = async () => {
    setPayId(null);
    setPayValue(null);
    setBalance(null);
    setFullname(null);
    setClientId(null);
    setPayDate(null);
    setOpenEditar(false);

    // Volver a cargar los pagos después de editar
    await fetchPays();
  };

  return (
    <HeaderResponsive sx={{ pb: { xs: 10, md: 8 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Generar Informe Abonos</Title>
        <Button variant="contained" onClick={submit} startIcon={isMobile ? null : <SearchIcon />}>
          {isMobile ? <SearchIcon /> : 'Buscar'}
        </Button>
      </Box>
      <Card sx={{ width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha Inicial"
                type="date"
                value={start_date}
                onChange={(e) => setStart_date(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                fullWidth
                size="small"
                required
                inputProps={{ max: minYmd(end_date, nowOperationalDateString()) }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha Final"
                type="date"
                value={end_date}
                onChange={(e) => setEnd_date(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                fullWidth
                size="small"
                required
                inputProps={{ min: start_date, max: nowOperationalDateString() }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="form-layouts-separator-select-label">Seleccionar Oficina</InputLabel>
                <Select
                  label="Seleccionar Oficina"
                  value={office_id}
                  onChange={(e) => setOffice_id(e.target.value)}
                  required
                  size="small"
                >
                  {offices.map((office) => (
                    <MenuItem key={office.id} value={office.id}>
                      {office.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <Typography variant="body1" gutterBottom>
                  Seleccionar Cobros
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      indeterminate={selectedCollects.length > 0 && selectedCollects.length < collectsOffice.length}
                    />
                  }
                  label="Seleccionar todos"
                />
                <FormGroup>
                  <Grid container>
                    {collectsOffice.map((collect) => (
                      <FormControlLabel
                        key={collect.id}
                        control={
                          <Checkbox
                            checked={selectedCollects.includes(collect.id.toString())}
                            onChange={handleCheckboxChange}
                            value={collect.id.toString()}
                            required
                          />
                        }
                        label={collect.name}
                      />
                    ))}
                  </Grid>
                </FormGroup>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
        <CardContent
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto', // o usa valores reales de tu drawer
          }}
        >
          {pays.length > 0 ? (
            <>
              <SimpleCustomTable
                rows={rows}
                columns={displayColumns}
                countValue={dataRows.length}
                disableInternalScroll
                showBorders
                showCount
                exportCsv
                exportFileName="abonos-por-fecha.csv"
                wrapCellContent
                columnGroups={displayColumnGroups}
                getRowCellSx={({ row }) =>
                  (row as any).isGroupSeparator || (row as any).isTotal
                    ? {
                        backgroundColor: '#ffd3b8',
                        fontWeight: 700,
                      }
                    : undefined
                }
              />
            </>
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />
      <HistoricoModal
        open={openHistorico}
        onClose={handleCloseHistorico}
        clientId={clientId}
        collectId={collectId}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <EditPaysModalWeb
        open={openEditar}
        onClose={handleCloseEdit}
        clientId={Number(clientId)}
        payId={payId}
        payment={payValue}
        payDate={payDate}
        balance={balance}
        fullname={fullname}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />

    </HeaderResponsive>
  );
};

export default InformeAbonosAdd;
